import clsx from 'clsx';
import React from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import { toggleSwitch } from '../../reducers/switch';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import Button from '../../components/Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import DownloadIcon from '../../assets/icons/download-icon.svg?react';
import ArrowRightIcon from '../../assets/icons/arrow-right.svg?react';
import FileInput from '../../components/FileInput';
import moment from 'moment';

interface BulkUserCreationStepOneProps {
  otherClasses?: string;
  formHandler: any;
  handleSteps: React.Dispatch<number>;
  departments: { label: string; value: string }[];
  roles: { label: string; value: string }[];
  setNotExistDepartment: React.Dispatch<string[]>;
}

const BulkUserCreationStepOne: React.FC<BulkUserCreationStepOneProps> = ({
  otherClasses,
  formHandler,
  handleSteps,
  departments,
  roles,
  setNotExistDepartment,
}) => {
  const bulkUserCreationStepOneClasses = clsx(otherClasses);
  const dispatch = useDispatch();
  const lg = useButtonSizeLarge();

  const normalizeHeader = (header: string) => {
    const normalized = header.toLowerCase().trim();

    // Handle headers from the image
    if (normalized.includes('employee id') || normalized === 'id')
      return 'employeeId';
    if (normalized.includes('full name') || normalized === 'name')
      return 'fullName';
    if (normalized.includes('email') || normalized.includes('address'))
      return 'email';
    if (
      normalized.includes('contact') ||
      normalized.includes('phone') ||
      normalized.includes('number')
    )
      return 'contactNumber';
    if (
      normalized.includes('date of birth') ||
      normalized.includes('dob') ||
      normalized.includes('birth')
    )
      return 'dateOfBirth';
    if (
      normalized.includes('date of hire') ||
      normalized.includes('doh') ||
      normalized.includes('hire')
    )
      return 'dateOfHire';
    if (normalized.includes('department')) return 'department';
    if (normalized.includes('job title') || normalized.includes('title'))
      return 'jobTitle';
    if (normalized.includes('role')) return 'role';
    return header;
  };

  const handleFileChange = (file: File | null) => {
    const departmentsLabels: string[] = [];
    if (!file) {
      return;
    }
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension === 'csv') {
      Papa.parse(file, {
        complete: (result) => {
          const dataToSend = result?.data?.map((node: any) => {
            const normalizedNode: any = {};
            Object.keys(node).forEach((key) => {
              const normalizedKey = normalizeHeader(key) as string;
              normalizedNode[normalizedKey] = node[key] ?? '';
            });

            const department = departments.find(
              (department) =>
                department.label?.toLowerCase().trim() ===
                (normalizedNode.department ?? '').toLowerCase().trim(),
            );
            const role = roles.find(
              (role) =>
                role.label?.toLowerCase().trim() ===
                (normalizedNode.role ?? '').toLowerCase().trim(),
            );
            departmentsLabels.push(normalizedNode.department ?? '');
            normalizedNode.department = department?.value ?? '';
            normalizedNode.role = role?.value ?? '';

            if (normalizedNode.dateOfBirth) {
              let parsedDateOfBirth: string | null = null;

              if (typeof normalizedNode.dateOfBirth === 'number') {
                const excelEpoch = moment('1900-01-01');
                const serial =
                  normalizedNode.dateOfBirth > 59
                    ? normalizedNode.dateOfBirth - 1
                    : normalizedNode.dateOfBirth;
                parsedDateOfBirth = excelEpoch
                  .clone()
                  .add(serial - 1, 'days')
                  .format('YYYY-MM-DD');
              } else if (
                moment(
                  normalizedNode.dateOfBirth,
                  moment.ISO_8601,
                  true,
                ).isValid()
              ) {
                parsedDateOfBirth = moment(normalizedNode.dateOfBirth).format(
                  'YYYY-MM-DD',
                );
              } else if (
                moment(
                  normalizedNode.dateOfBirth,
                  ['DD-MM-YYYY', 'MM/DD/YYYY'],
                  true,
                ).isValid()
              ) {
                parsedDateOfBirth = moment(normalizedNode.dateOfBirth, [
                  'DD-MM-YYYY',
                  'MM/DD/YYYY',
                ]).format('YYYY-MM-DD');
              } else if (
                moment(
                  normalizedNode.dateOfBirth,
                  'MMM-DD-YYYY',
                  true,
                ).isValid()
              ) {
                parsedDateOfBirth = moment(
                  normalizedNode.dateOfBirth,
                  'MMM-DD-YYYY',
                ).format('YYYY-MM-DD');
              }

              // ✅ Validation checks
              if (parsedDateOfBirth) {
                const birthMoment = moment(parsedDateOfBirth);
                const today = moment();

                // ❌ 1. Future date check
                if (birthMoment.isAfter(today, 'day')) {
                  normalizedNode.dateOfBirth = '';
                }
                // // ❌ 2. Under 18 years old check
                // else if (today.diff(birthMoment, 'years') < 18) {
                //   normalizedNode.dateOfBirth = '';
                // }
                // // ✅ Valid date of birth
                else {
                  normalizedNode.dateOfBirth = parsedDateOfBirth;
                }
              } else {
                normalizedNode.dateOfBirth = '';
              }
            } else {
              normalizedNode.dateOfBirth = '';
            }

            // Check if dateOfHire is valid and not in the future
            // if (
            //   normalizedNode.dateOfHire &&
            //   !isNaN(Date.parse(normalizedNode.dateOfHire))
            // ) {
            //   const dohDate = new Date(normalizedNode.dateOfHire);
            //   const today = new Date();
            //   dohDate.setHours(0, 0, 0, 0);
            //   today.setHours(0, 0, 0, 0);
            //   normalizedNode.dateOfHire =
            //     dohDate > today ? '' : dohDate.toISOString().split('T')[0];
            // } else {
            //   normalizedNode.dateOfHire = '';
            // }

            if (normalizedNode.dateOfHire) {
              let parsedDateOfHire: string | null = null;

              if (typeof normalizedNode.dateOfHire === 'number') {
                const excelEpoch = moment('1900-01-01');
                const serial =
                  normalizedNode.dateOfHire > 59
                    ? normalizedNode.dateOfHire - 1
                    : normalizedNode.dateOfHire;
                parsedDateOfHire = excelEpoch
                  .clone()
                  .add(serial - 1, 'days')
                  .format('YYYY-MM-DD');
              } else if (
                moment(
                  normalizedNode.dateOfHire,
                  moment.ISO_8601,
                  true,
                ).isValid()
              ) {
                parsedDateOfHire = moment(normalizedNode.dateOfHire).format(
                  'YYYY-MM-DD',
                );
              } else if (
                moment(
                  normalizedNode.dateOfHire,
                  ['DD-MM-YYYY', 'MM/DD/YYYY'],
                  true,
                ).isValid()
              ) {
                parsedDateOfHire = moment(normalizedNode.dateOfHire, [
                  'DD-MM-YYYY',
                  'MM/DD/YYYY',
                ]).format('YYYY-MM-DD');
              } else if (
                moment(normalizedNode.dateOfHire, 'MMM-DD-YYYY', true).isValid()
              ) {
                parsedDateOfHire = moment(
                  normalizedNode.dateOfHire,
                  'MMM-DD-YYYY',
                ).format('YYYY-MM-DD');
              }

              // Check if parsed date is in the future
              if (
                parsedDateOfHire &&
                moment(parsedDateOfHire).isAfter(moment(), 'day')
              ) {
                normalizedNode.dateOfHire = '';
              } else if (parsedDateOfHire) {
                normalizedNode.dateOfHire = parsedDateOfHire;
              } else {
                normalizedNode.dateOfHire = '';
              }
            } else {
              normalizedNode.dateOfHire = '';
            }

            if (
              normalizedNode.dateOfBirth &&
              normalizedNode.dateOfHire &&
              moment(normalizedNode.dateOfHire).isBefore(
                moment(normalizedNode.dateOfBirth).add(18, 'years'),
              )
            ) {
              normalizedNode.dateOfHire = '';
            }
            return normalizedNode;
          });

          const requiredFields = [
            'employeeId',
            'fullName',
            'email',
            'contactNumber',
            'dateOfBirth',
            'dateOfHire',
            'department',
            'jobTitle',
            'role',
          ];

          const filteredDataToSend = dataToSend.filter((row: any) => {
            return requiredFields.some(
              (field) =>
                row[field] !== undefined &&
                row[field] !== null &&
                String(row[field]).trim() !== '',
            );
          });
          setNotExistDepartment(departmentsLabels);
          if (filteredDataToSend.length === 0) {
            formHandler?.setFieldValue('userRecords', [
              {
                employeeId: '',
                fullName: '',
                email: '',
                contactNumber: '',
                dateOfBirth: '',
                dateOfHire: '',
                department: '',
                jobTitle: '',
                role: '',
              },
            ]);
          } else {
            formHandler?.setFieldValue('userRecords', filteredDataToSend);
          }
        },

        header: true,
      });
    } else if (fileExtension === 'xlsx') {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const allKeys = Array.from(
          new Set(
            jsonData.reduce((acc: string[], node: any) => {
              return acc.concat(Object.keys(node));
            }, []),
          ),
        );

        const dataToSend = jsonData?.map((node: any) => {
          const normalizedNode: any = {};

          // Normalize all keys and handle empty values
          allKeys.forEach((key) => {
            const normalizedKey = normalizeHeader(key) as string;
            normalizedNode[normalizedKey] =
              node[key] !== undefined && node[key] !== null ? node[key] : '';
          });

          // Handle department mapping (case-insensitive)
          const department = departments.find(
            (department) =>
              department.label?.toLowerCase().trim() ===
              (normalizedNode.department ?? '').toLowerCase().trim(),
          );

          departmentsLabels.push(normalizedNode.department ?? '');
          normalizedNode.department = department?.value ?? '';

          // Handle role mapping
          const role = roles.find(
            (role) =>
              role.label?.toLowerCase().trim() ===
              (normalizedNode.role ?? '').toLowerCase().trim(),
          );
          normalizedNode.role = role?.value ?? '';

          // Enhanced date handling with Moment.js
          // Handle dateOfBirth
          if (normalizedNode.dateOfBirth) {
            let parsedDateOfBirth: string | null = null;

            if (typeof normalizedNode.dateOfBirth === 'number') {
              const excelEpoch = moment('1900-01-01');
              const serial =
                normalizedNode.dateOfBirth > 59
                  ? normalizedNode.dateOfBirth - 1
                  : normalizedNode.dateOfBirth;
              parsedDateOfBirth = excelEpoch
                .clone()
                .add(serial - 1, 'days')
                .format('YYYY-MM-DD');
            } else if (
              moment(
                normalizedNode.dateOfBirth,
                moment.ISO_8601,
                true,
              ).isValid()
            ) {
              parsedDateOfBirth = moment(normalizedNode.dateOfBirth).format(
                'YYYY-MM-DD',
              );
            } else if (
              moment(
                normalizedNode.dateOfBirth,
                ['DD-MM-YYYY', 'MM/DD/YYYY'],
                true,
              ).isValid()
            ) {
              parsedDateOfBirth = moment(normalizedNode.dateOfBirth, [
                'DD-MM-YYYY',
                'MM/DD/YYYY',
              ]).format('YYYY-MM-DD');
            } else if (
              moment(normalizedNode.dateOfBirth, 'MMM-DD-YYYY', true).isValid()
            ) {
              parsedDateOfBirth = moment(
                normalizedNode.dateOfBirth,
                'MMM-DD-YYYY',
              ).format('YYYY-MM-DD');
            }

            // ✅ Validation checks
            if (parsedDateOfBirth) {
              const birthMoment = moment(parsedDateOfBirth);
              const today = moment();

              // ❌ 1. Future date check
              if (birthMoment.isAfter(today, 'day')) {
                normalizedNode.dateOfBirth = '';
              }
              // // ❌ 2. Under 18 years old check
              // else if (today.diff(birthMoment, 'years') < 18) {
              //   normalizedNode.dateOfBirth = '';
              // }
              // // ✅ Valid date of birth
              else {
                normalizedNode.dateOfBirth = parsedDateOfBirth;
              }
            } else {
              normalizedNode.dateOfBirth = '';
            }
          } else {
            normalizedNode.dateOfBirth = '';
          }

          // Handle dateOfHire
          if (normalizedNode.dateOfHire) {
            let parsedDateOfHire: string | null = null;

            if (typeof normalizedNode.dateOfHire === 'number') {
              const excelEpoch = moment('1900-01-01');
              const serial =
                normalizedNode.dateOfHire > 59
                  ? normalizedNode.dateOfHire - 1
                  : normalizedNode.dateOfHire;
              parsedDateOfHire = excelEpoch
                .clone()
                .add(serial - 1, 'days')
                .format('YYYY-MM-DD');
            } else if (
              moment(normalizedNode.dateOfHire, moment.ISO_8601, true).isValid()
            ) {
              parsedDateOfHire = moment(normalizedNode.dateOfHire).format(
                'YYYY-MM-DD',
              );
            } else if (
              moment(
                normalizedNode.dateOfHire,
                ['DD-MM-YYYY', 'MM/DD/YYYY'],
                true,
              ).isValid()
            ) {
              parsedDateOfHire = moment(normalizedNode.dateOfHire, [
                'DD-MM-YYYY',
                'MM/DD/YYYY',
              ]).format('YYYY-MM-DD');
            } else if (
              moment(normalizedNode.dateOfHire, 'MMM-DD-YYYY', true).isValid()
            ) {
              parsedDateOfHire = moment(
                normalizedNode.dateOfHire,
                'MMM-DD-YYYY',
              ).format('YYYY-MM-DD');
            }

            // Check if parsed date is in the future
            if (
              parsedDateOfHire &&
              moment(parsedDateOfHire).isAfter(moment(), 'day')
            ) {
              normalizedNode.dateOfHire = '';
            } else if (parsedDateOfHire) {
              normalizedNode.dateOfHire = parsedDateOfHire;
            } else {
              normalizedNode.dateOfHire = '';
            }
          } else {
            normalizedNode.dateOfHire = '';
          }

          if (
            normalizedNode.dateOfBirth &&
            normalizedNode.dateOfHire &&
            moment(normalizedNode.dateOfHire).isBefore(
              moment(normalizedNode.dateOfBirth).add(18, 'years'),
            )
          ) {
            normalizedNode.dateOfHire = '';
          }

          return normalizedNode;
        });

        const requiredFields = [
          'employeeId',
          'fullName',
          'email',
          'contactNumber',
          'dateOfBirth',
          'dateOfHire',
          'department',
          'jobTitle',
          'role',
        ];

        const filteredDataToSend = dataToSend.filter((row: any) => {
          return requiredFields.some(
            (field) =>
              row[field] !== undefined &&
              row[field] !== null &&
              String(row[field]).trim() !== '',
          );
        });
        setNotExistDepartment(departmentsLabels);

        if (filteredDataToSend.length === 0) {
          formHandler?.setFieldValue('userRecords', [
            {
              employeeId: '',
              fullName: '',
              email: '',
              contactNumber: '',
              dateOfBirth: '',
              dateOfHire: '',
              department: '',
              jobTitle: '',
              role: '',
            },
          ]);
        } else {
          formHandler?.setFieldValue('userRecords', filteredDataToSend);
        }
      };
      reader.readAsBinaryString(file);
    } else {
      alert('Unsupported file type. Please upload CSV or XLSX.');
    }
  };

  return (
    <div
      className={bulkUserCreationStepOneClasses}
      data-testid="bulk-user-creation-step-one"
    >
      <div className="flex w-full flex-col items-stretch justify-between gap-15 sm:flex-row sm:items-center lg:gap-6">
        <div>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
              onClick={() => dispatch(toggleSwitch())}
            >
              <ToggleIcon />
            </button>
            <h1 className="h3">Bulk User Creation</h1>
          </div>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 text-lg leading-5 font-medium lg:block lg:text-xl lg:leading-6">
            Instructions for Bulk User Creation
          </p>
        </div>
        <a
          href="/sample.xlsx"
          download="sample.xlsx"
          className="global-transition bg-brand-byzantine shadow-button-box-shadow text-light-neutral-tones-background font-inter-variable hover:border-brand-byzantine hover:text-brand-byzantine disabled:bg-additional-disable disabled:text-neutral-tones-text/50 disabled:hover:bg-additional-disable flex cursor-pointer items-center justify-center gap-2.5 rounded-lg border-2 border-transparent p-2.5 text-base font-bold hover:bg-white disabled:cursor-not-allowed disabled:hover:border-transparent md:px-2.5 md:py-15 md:text-lg lg:px-25 lg:py-15 lg:text-xl/6"
        >
          <DownloadIcon className="h-6 w-6 [&>g>path]:fill-current" /> Download
          Sample File
        </a>
      </div>
      <div className="mt-8 sm:mt-12">
        <div className="w-full max-w-741">
          <h6 className="text-inter-variable text-neutral-tones-text text-xl leading-7 font-bold">
            XLSX or CSV File Format
          </h6>
          <p className="text-neutral-tones-text/80 font-inter-variable my-4 text-xl leading-7 font-normal">
            The CSV or XLSX file should contain the following columns in the
            specified order: Headers would be Employee ID, Full Name, Email,
            Contact Number, Date of birth, Date of Hire, Department, Job Title
            and Role
          </p>
          <ul className="list-disc pl-5">
            <li className="text-neutral-tones-text/80 font-inter-variable text-xl leading-7 font-normal">
              <strong className="text-neutral-tones-text font-bold">
                Employee ID:
              </strong>{' '}
              The User’s Employee Id e.g. ( EMP-989776)
            </li>
            <li className="text-neutral-tones-text/80 font-inter-variable text-xl leading-7 font-normal">
              <strong className="text-neutral-tones-text font-bold">
                Full Name:
              </strong>{' '}
              The user's full name, e.g. (John Sturgis )
            </li>
            <li className="text-neutral-tones-text/80 font-inter-variable text-xl leading-7 font-normal">
              <strong className="text-neutral-tones-text font-bold">
                Email:
              </strong>{' '}
              The user's email address (must be a valid email format).
            </li>
            <li className="text-neutral-tones-text/80 font-inter-variable text-xl leading-7 font-normal">
              <strong className="text-neutral-tones-text font-bold">
                Contact Number:
              </strong>{' '}
              The user's complete phone number, e.g. ( 718 222 2222 )
            </li>
            <li className="text-neutral-tones-text/80 font-inter-variable text-xl leading-7 font-normal">
              <strong className="text-neutral-tones-text font-bold">
                Date of Birth:
              </strong>{' '}
              The user's date of birth, e.g. ( 1998-11-22 )
            </li>
            <li className="text-neutral-tones-text/80 font-inter-variable text-xl leading-7 font-normal">
              <strong className="text-neutral-tones-text font-bold">
                Date of Hire:
              </strong>{' '}
              The user's date of hire, e.g. ( 1998-11-22 )
            </li>
            <li className="text-neutral-tones-text/80 font-inter-variable text-xl leading-7 font-normal">
              <strong className="text-neutral-tones-text font-bold">
                Department:
              </strong>{' '}
              The user's department, e.g. ( Quality Control )
            </li>
            <li className="text-neutral-tones-text/80 font-inter-variable text-xl leading-7 font-normal">
              <strong className="text-neutral-tones-text font-bold">
                Job Title:
              </strong>{' '}
              The user's Job Title, e.g. ( Engineer )
            </li>
            <li className="text-neutral-tones-text/80 font-inter-variable text-xl leading-7 font-normal">
              <strong className="text-neutral-tones-text font-bold">
                Role:
              </strong>{' '}
              The user's Role (Admin, Manager, or Employee).
            </li>
          </ul>
        </div>
        <img
          src="/assets/images/bulk-user-sample-xlsx.png"
          alt="bulk-user-sample-xlsx"
          className="mt-10 hidden w-full md:block"
        />
        <FileInput
          label="Upload File"
          name="file"
          selectedFiles={formHandler?.values?.file}
          onChange={(e) => {
            handleFileChange(e?.[0] as File);
            formHandler?.setFieldValue('file', e, true);
          }}
          otherClasses={{ container: 'mt-10' }}
          accept={{
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
              ['.xlsx'],
            'text/csv': ['.csv'],
          }}
          onBlur={formHandler?.handleBlur}
          customPlaceholder={{
            label1: 'Max 1 file allowed',
            label3: 'Upload or attach XLSX/CSV file',
          }}
          multiple={false}
          errorMessage={
            formHandler?.errors?.file ? formHandler?.errors?.file : ''
          }
        />
        <Button
          size={lg}
          type="button"
          onClick={() => handleSteps(2)}
          otherClasses="mx-auto lg:mr-0 lg:ml-auto mt-10 w-full sm:w-150"
          disable={
            !formHandler?.values?.file || formHandler.values.file.length === 0
          }
        >
          Next <ArrowRightIcon className="h-6 w-6 [&>g>path]:fill-current" />
        </Button>
      </div>
    </div>
  );
};

export default BulkUserCreationStepOne;
